import { CheckCircle2, AlertTriangle, ClipboardCheck, Wallet, UserCheck, UserX } from "lucide-react";
import type { VehicleExpert } from "@/lib/vehicle-expert";

/** AK:s egen bedömning av ett enskilt fordon — kontrollprotokoll, för/nackdelar,
 *  vem det passar och ägarkostnad. Renderas serverside så Google ser allt. */
export function VehicleExpertBlock({ expert, title }: { expert: VehicleExpert; title: string }) {
  return (
    <section className="border-t border-border bg-card/40">
      <div className="container-edge space-y-8 py-14 md:py-16">
        <div className="max-w-3xl">
          <p className="eyebrow">AK:s bedömning</p>
          <h2 className="mt-1 font-display text-2xl md:text-3xl">Vår genomgång av {title}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-foreground/90">{expert.verdict}</p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="inline-flex items-center gap-2 font-display text-xl">
              <CheckCircle2 size={18} className="text-emerald-600" /> Starka sidor
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm leading-relaxed">
              {expert.pros.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 size={14} className="mt-1 shrink-0 text-emerald-600" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="inline-flex items-center gap-2 font-display text-xl">
              <AlertTriangle size={18} className="text-amber-600" /> Räkna med detta
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm leading-relaxed">
              {expert.cons.length > 0 ? (
                expert.cons.map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <AlertTriangle size={14} className="mt-1 shrink-0 text-amber-600" />
                    <span>{c}</span>
                  </li>
                ))
              ) : (
                <li>Inget att anmärka utöver normalt underhåll för årsmodellen.</li>
              )}
            </ul>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="inline-flex items-center gap-2 font-display text-xl">
              <UserCheck size={18} className="text-emerald-600" /> Passar
            </h3>
            <ul className="mt-4 list-disc space-y-1.5 pl-5 text-sm leading-relaxed">
              {expert.suitedFor.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="inline-flex items-center gap-2 font-display text-xl">
              <UserX size={18} className="text-muted-foreground" /> Passar mindre bra
            </h3>
            <ul className="mt-4 list-disc space-y-1.5 pl-5 text-sm leading-relaxed">
              {expert.notSuitedFor.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6">
          <h3 className="inline-flex items-center gap-2 font-display text-xl">
            <ClipboardCheck size={18} className="text-accent" /> Kontrollprotokoll före försäljning
          </h3>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Kontroll</th>
                  <th className="py-2 pr-4 font-medium">Resultat</th>
                  <th className="py-2 font-medium">Så gör vi</th>
                </tr>
              </thead>
              <tbody>
                {expert.checks.map((c) => (
                  <tr key={c.label} className="border-b border-border/60 align-top">
                    <td className="py-3 pr-4 font-medium">{c.label}</td>
                    <td className="py-3 pr-4 text-emerald-700">{c.value}</td>
                    <td className="py-3 text-muted-foreground">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6">
          <h3 className="inline-flex items-center gap-2 font-display text-xl">
            <Wallet size={18} className="text-accent" /> Vad kostar det att äga?
          </h3>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Post</th>
                  <th className="py-2 pr-4 font-medium">Uppskattning</th>
                  <th className="py-2 font-medium">Underlag</th>
                </tr>
              </thead>
              <tbody>
                {expert.ownerCosts.map((c) => (
                  <tr key={c.label} className="border-b border-border/60 align-top">
                    <td className="py-3 pr-4 font-medium">{c.label}</td>
                    <td className="py-3 pr-4">{c.value}</td>
                    <td className="py-3 text-muted-foreground">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Siffrorna är AK Husbil &amp; Fritids egna erfarenhetstal från Kalix och Norrbotten, inte
            tillverkarens. De uppdateras när våra kostnader gör det.
          </p>
        </div>
      </div>
    </section>
  );
}
